import React, { useState, useEffect } from 'react';

interface Contact {
  id: string;
  phoneNumber: string;
  name?: string;
  email?: string;
}

interface AllContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contacts: string[]) => void;
}

export const AllContactsModal: React.FC<AllContactsModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadContacts();
    }
  }, [isOpen]);

  const loadContacts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // TODO: Replace with actual API call to fetch contacts
      const mockContacts: Contact[] = [
        { id: '1', phoneNumber: '+1234567890', name: 'John Doe' },
        { id: '2', phoneNumber: '+1987654321', name: 'Jane Smith' },
        // Add more mock contacts as needed
      ];
      setContacts(mockContacts);
    } catch (err) {
      setError('Failed to load contacts. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(contact => contact.phoneNumber));
    }
  };

  const handleSave = () => {
    if (selectedContacts.length > 0) {
      onSave(selectedContacts);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">All Contacts</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Select All Option */}
        <div className="mb-4">
          <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
            <input
              type="checkbox"
              checked={selectedContacts.length === contacts.length}
              onChange={handleSelectAll}
              className="h-4 w-4 text-[#FF5722] border-gray-300 rounded focus:ring-[#FF5722]"
            />
            <span className="text-sm font-medium text-gray-700">
              Add all contacts ({contacts.length})
            </span>
          </label>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
            {error}
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF5722]"></div>
          </div>
        ) : (
          /* Contacts List */
          <div className="border rounded-md p-4 max-h-96 overflow-y-auto">
            {contacts.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No contacts found</p>
            ) : (
              <div className="space-y-2">
                {contacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedContacts.includes(contact.phoneNumber)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedContacts([...selectedContacts, contact.phoneNumber]);
                        } else {
                          setSelectedContacts(selectedContacts.filter(num => num !== contact.phoneNumber));
                        }
                      }}
                      className="h-4 w-4 text-[#FF5722] border-gray-300 rounded focus:ring-[#FF5722]"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{contact.name || 'Unnamed Contact'}</p>
                      <p className="text-sm text-gray-500">{contact.phoneNumber}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={selectedContacts.length === 0}
            className={`px-4 py-2 rounded-md transition-colors ${
              selectedContacts.length > 0
                ? 'bg-[#FF5722] text-white hover:bg-[#F4511E]'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            Add Selected Contacts
          </button>
        </div>
      </div>
    </div>
  );
}; 